// ============================================
// Risk Radar — Flow toxicity layer model (phases 1 and 2)
//
// Turns one radar.flow.symbols payload into what the page shows. Pure
// functions only, like model.ts.
//
// TWO MEASURES (docs/NexRisk_Risk_Radar_Flow_API.md v1.1), one colour at a time:
//
//   Toxic share (phase 1, profile-based)
//     per instrument, over the backend's rolling window, the share of its
//     B-Book flow (USD notional, both platforms) from accounts whose latest
//     classification is toxic: b_toxic_share against `bands` (%).
//
//   Markout (phase 2, price-based)
//     for every client deal, how far the mid moved the client's way 1 / 5 /
//     30 s after the fill; positive = against the broker. Volume-weighted in
//     bps: b_markout_bps["5"] against `markout_bands` (bps). Below `min_fills`
//     deals the backend sends null: "not enough fills", never a guess.
//
// NOTHING IS GUESSED:
//   no_b_flow     — flow in the window, none of it in the B-Book: no share;
//   thin          — markout mode, B-Book flow but fewer markouts than min_fills;
//   unclassified  — accounts with no classification are never flagged; their
//                   flow is shown apart so "not classified" never reads "clean".
//
// PLATFORMS:
//   Metrics are per platform. With `by_platform` on the rows and totals
//   (backend, 20 Oct 2026), the MT5 / cTrader views read that platform's own
//   figures: share, markout, accounts, classes, totals. "Both" reads the
//   row's own figures. Without `by_platform` (older backend) the figures stay
//   both platforms together and the page says so (FlowView.perPlatform false).
// ============================================

import type { FlowPayload, FlowSymbolRow, FlowHorizons, FlowPlatformFigures } from '@/services/api';
import { bandOf, heatOf, type Band, type Platform, type PlatformScope, type Thresholds } from './model';

export type FlowMode = 'share' | 'markout';
export type FlowState = 'rated' | 'no_b_flow' | 'thin';

export const HORIZONS = ['1', '5', '30'] as const;
export type Horizon = typeof HORIZONS[number];

export interface FlowClassPart {
  cls: string;
  flowUsd: number;
  fills: number;
  accounts: number;
  /** In the broker's toxic set (a flagged account of it still needs the confidence / level). */
  toxicClass: boolean;
}

/** Markout figures per horizon; null = not measured (or below min_fills for bps). */
export interface Markout {
  usd: Record<Horizon, number | null>;
  bps: Record<Horizon, number | null>;
  bUsd: Record<Horizon, number | null>;
  bBps: Record<Horizon, number | null>;
  flaggedUsd: Record<Horizon, number | null>;
  fills: Record<Horizon, number>;
}

/**
 * What the flow cost the broker, B-Book, USD (Flow API v1.3 §6). Positive =
 * cost to the broker. net = price + rebates + client rebates − commission − fees,
 * summed over the parts known; `complete` false when a part is not known.
 */
export interface FlowCost {
  /** b_effective_markout_usd["5"]: price move after the fill, spread paid included. */
  price: number | null;
  rebate: number | null;
  clientRebate: number | null;
  commission: number | null;
  fee: number | null;
  aRebate: number | null;
  flaggedCommission: number | null;
  flaggedRebate: number | null;
  coverage: number | null;
  basis: string | null;
  net: number | null;
  complete: boolean;
}

/** The parts of the net cost, broker view. null when the payload carries no cost fields (backend before v1.3). */
export function costOf(r: {
  b_effective_markout_usd?: FlowHorizons; commission_usd?: number | null; fee_usd?: number | null;
  rebate_usd?: number | null; client_rebate_usd?: number | null; a_rebate_usd?: number | null;
  flagged_commission_usd?: number | null; flagged_rebate_usd?: number | null;
  cost_coverage?: number | null; rebate_basis?: string | null;
} | null | undefined): FlowCost | null {
  if (!r || !('commission_usd' in r || 'rebate_usd' in r)) return null;
  const price = optNum(r.b_effective_markout_usd?.['5']);
  const rebate = optNum(r.rebate_usd), clientRebate = optNum(r.client_rebate_usd);
  const commission = optNum(r.commission_usd), fee = optNum(r.fee_usd);
  // client_rebate_usd is cTrader only: absent is not "unknown".
  const parts = [price, rebate, commission, fee, ...(r.client_rebate_usd !== undefined ? [clientRebate] : [])];
  const known = parts.filter((v): v is number => v !== null);
  // known.length < parts.length: the net counts only the parts with a figure.
  return {
    price, rebate, clientRebate, commission, fee,
    aRebate: optNum(r.a_rebate_usd),
    flaggedCommission: optNum(r.flagged_commission_usd), flaggedRebate: optNum(r.flagged_rebate_usd),
    coverage: optNum(r.cost_coverage),
    basis: typeof r.rebate_basis === 'string' ? r.rebate_basis : null,
    // Without the price part there is no net: commission alone would read as "the broker earned".
    net: price === null ? null : price + (rebate ?? 0) + (clientRebate ?? 0) - (commission ?? 0) - (fee ?? 0),
    complete: known.length === parts.length,
  };
}

export interface FlowSymbol {
  symbol: string;
  /** In the colour mode chosen. */
  state: FlowState;
  band: Band | null;
  heat: number;
  /** 1-based rank, in the colour mode chosen, among instruments that have something against the broker. */
  rank: number | null;

  /** b_toxic_share, 0..1; null without B-Book flow. */
  bShare: number | null;
  /** toxic_share, all books, 0..1; null without flow. */
  share: number | null;
  shareBand: Band | null;

  flowUsd: number;
  flaggedUsd: number;
  bFlowUsd: number;
  bFlaggedUsd: number;
  fills: number;
  flaggedFills: number;
  unpricedFills: number;
  accounts: number;
  flaggedAccounts: number;
  unclassifiedAccounts: number;
  unclassifiedUsd: number;
  /** unclassified flow ÷ flow, 0..1; null without flow. */
  unclassifiedShare: number | null;

  markout: Markout;
  /** v1.3: cost to the broker; null on an older backend. */
  cost: FlowCost | null;
  /** b_markout_bps["5"] in its band; null when not measured. */
  markoutBand: Band | null;

  /** Per platform: all-books flow and its flagged part. */
  parts: Array<{ platform: Platform; flowUsd: number; flaggedUsd: number }>;
  byClass: FlowClassPart[];
}

export interface FlowView {
  mode: FlowMode;
  asOf: number | null;
  windowSec: number | null;
  /** Toxic-share bands (fractions). */
  thresholds: Thresholds;
  /** Markout bands (bps). */
  markoutThresholds: Thresholds;
  minFills: number | null;
  /** The payload carries markout figures (backend 1.1 and later). */
  hasMarkout: boolean;
  /** The toxic set in force, as the backend echoes it. */
  toxicClasses: Array<{ cls: string; minConfidence: number | null; minRiskLevel: string | null }>;
  platforms: Platform[];
  /** The figures are the platform chosen's own (scope MT5 / cTrader and a backend that splits them). */
  perPlatform: boolean;
  symbols: FlowSymbol[];
  bySymbol: Map<string, FlowSymbol>;
  /** Instruments traded in the window that the platform chosen leaves out. */
  hiddenByScope: number;
  /** Whole book, both platforms (the payload's totals). */
  totals: {
    flowUsd: number; flaggedUsd: number; bFlowUsd: number; bFlaggedUsd: number;
    share: number | null; bShare: number | null;
    fills: number; flaggedFills: number; unpricedFills: number;
    accounts: number; flaggedAccounts: number; unclassifiedAccounts: number;
    unclassifiedUsd: number;
    markout: Markout;
    cost: FlowCost | null;
  };
  /** Book band in the colour mode chosen. */
  bookBand: Band | null;
  bandCounts: Record<Band, number>;
  rated: number;
  noBFlow: number;
  thin: number;
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function optNum(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function parseTime(text: unknown): number | null {
  if (typeof text !== 'string' || !text) return null;
  const ms = Date.parse(text);
  return Number.isFinite(ms) ? ms : null;
}

/** The backend's documented defaults (placeholders until Ross sets them). */
export const DEFAULT_FLOW_THRESHOLDS: Thresholds = { warning: 0.10, high: 0.25, critical: 0.40 };
export const DEFAULT_MARKOUT_THRESHOLDS: Thresholds = { warning: 0.5, high: 1, critical: 2 };

export function flowThresholds(payload: FlowPayload | null): Thresholds {
  const b = payload?.bands;
  if (!b) return DEFAULT_FLOW_THRESHOLDS;
  const w = num(b.warning_pct) / 100, h = num(b.high_pct) / 100, c = num(b.critical_pct) / 100;
  return w > 0 && w < h && h <= c ? { warning: w, high: h, critical: c } : DEFAULT_FLOW_THRESHOLDS;
}

export function markoutThresholds(payload: FlowPayload | null): Thresholds {
  const b = payload?.markout_bands;
  if (!b) return DEFAULT_MARKOUT_THRESHOLDS;
  const w = num(b.warning_bps), h = num(b.high_bps), c = num(b.critical_bps);
  return w > 0 && w < h && h <= c ? { warning: w, high: h, critical: c } : DEFAULT_MARKOUT_THRESHOLDS;
}

function toPlatform(v: unknown): Platform | null {
  if (v === 'MT5') return 'MT5';
  if (typeof v === 'string' && /ctrader/i.test(v)) return 'CTRADER';
  return null;
}

function horizonsOpt(h: FlowHorizons | null | undefined): Record<Horizon, number | null> {
  return { '1': optNum(h?.['1']), '5': optNum(h?.['5']), '30': optNum(h?.['30']) };
}

function horizonsNum(h: FlowHorizons | null | undefined): Record<Horizon, number> {
  return { '1': num(h?.['1']), '5': num(h?.['5']), '30': num(h?.['30']) };
}

function markoutOf(r: Partial<FlowPlatformFigures> | null | undefined): Markout {
  return {
    usd: horizonsOpt(r?.markout_usd),
    bps: horizonsOpt(r?.markout_bps),
    bUsd: horizonsOpt(r?.b_markout_usd),
    bBps: horizonsOpt(r?.b_markout_bps),
    flaggedUsd: horizonsOpt(r?.flagged_markout_usd),
    fills: horizonsNum(r?.markout_fills),
  };
}

/** A markout is a reading only above 0: at or below 0 the price moved the broker's way. */
function markoutLevel(bps: number | null): number {
  return bps !== null && bps > 0 ? bps : 0;
}

/** All-books flow per platform, from by_platform when the backend sends it. */
function partsOf(r: FlowSymbolRow): FlowSymbol['parts'] {
  const parts: FlowSymbol['parts'] = [];
  const bp = r.by_platform;
  if (bp) {
    for (const p of ['MT5', 'CTRADER'] as const) {
      const o = bp[p];
      if (o && num(o.flow_usd) > 0.5) parts.push({ platform: p, flowUsd: num(o.flow_usd), flaggedUsd: num(o.flagged_flow_usd) });
    }
    return parts;
  }
  const flowUsd = num(r.flow_usd), flaggedUsd = num(r.flagged_flow_usd);
  const ctFlow = num(r.flow_usd_ctrader), ctFlagged = num(r.flagged_flow_usd_ctrader);
  if (flowUsd - ctFlow > 0.5) parts.push({ platform: 'MT5', flowUsd: flowUsd - ctFlow, flaggedUsd: Math.max(0, flaggedUsd - ctFlagged) });
  if (ctFlow > 0.5) parts.push({ platform: 'CTRADER', flowUsd: ctFlow, flaggedUsd: ctFlagged });
  return parts;
}

/** The figures to read for a row: the platform's own when split and chosen, else the row's. */
function figuresOf(r: FlowSymbolRow, scope: PlatformScope): (FlowPlatformFigures & { by_class?: FlowSymbolRow['by_class'] }) | null {
  if (scope === 'ALL' || !r.by_platform) return r;
  return r.by_platform[scope] ?? null;
}

function rowOf(row: FlowSymbolRow, src: FlowPlatformFigures, mode: FlowMode, t: Thresholds, mt: Thresholds, toxic: Set<string>): FlowSymbol {
  const r = src;
  const flowUsd = num(r.flow_usd);
  const bFlowUsd = num(r.b_flow_usd);
  const bShare = optNum(r.b_toxic_share);
  const flaggedUsd = num(r.flagged_flow_usd);
  const parts = partsOf(row);

  const byClass: FlowClassPart[] = Object.entries(r.by_class ?? {})
    .map(([cls, v]) => ({ cls, flowUsd: num(v?.flow_usd), fills: num(v?.fills), accounts: num(v?.accounts), toxicClass: toxic.has(cls) }))
    .sort((a, b) => b.flowUsd - a.flowUsd || a.cls.localeCompare(b.cls));

  const markout = markoutOf(r);
  const mBps = markout.bBps['5'];
  const markoutBand = mBps === null ? null : bandOf(markoutLevel(mBps), mt);
  const shareBand = bShare === null ? null : bandOf(bShare, t);

  let state: FlowState;
  let band: Band | null;
  let heat: number;
  if (mode === 'share') {
    state = bShare === null ? 'no_b_flow' : 'rated';
    band = shareBand;
    heat = bShare === null ? 0 : heatOf(bShare, t);
  } else {
    state = bFlowUsd <= 0 ? 'no_b_flow' : mBps === null ? 'thin' : 'rated';
    band = state === 'rated' ? markoutBand : null;
    heat = state === 'rated' ? heatOf(markoutLevel(mBps), mt) : 0;
  }

  const unclassifiedUsd = num(r.unclassified_flow_usd);
  return {
    symbol: row.symbol, state, band, heat, rank: null,
    bShare, share: optNum(r.toxic_share), shareBand,
    flowUsd, flaggedUsd, bFlowUsd, bFlaggedUsd: num(r.b_flagged_flow_usd),
    fills: num(r.fills), flaggedFills: num(r.flagged_fills), unpricedFills: num(r.unpriced_fills),
    accounts: num(r.accounts), flaggedAccounts: num(r.flagged_accounts),
    unclassifiedAccounts: num(r.unclassified_accounts), unclassifiedUsd,
    unclassifiedShare: flowUsd > 0 ? unclassifiedUsd / flowUsd : null,
    markout, markoutBand,
    cost: costOf(r),
    parts, byClass,
  };
}

function inScope(s: FlowSymbol, scope: PlatformScope): boolean {
  return scope === 'ALL' || s.parts.some((p) => p.platform === scope);
}

/** Whole-book figures of a payload for a scope: the platform's own totals when split. */
function totalsOf(payload: FlowPayload | null, scope: PlatformScope): Partial<FlowPlatformFigures> | null {
  const tt = payload?.totals;
  if (!tt) return null;
  if (scope === 'ALL' || !tt.by_platform) return tt;
  return tt.by_platform[scope] ?? {};
}

/** The payload splits its figures by platform. */
export function splitsPlatforms(payload: FlowPayload | null): boolean {
  return !!payload?.totals?.by_platform;
}

/** What ranks an instrument in the mode chosen: toxic B-Book flow, or B-Book markout against the broker at 5 s. */
export function rankValue(s: FlowSymbol, mode: FlowMode): number {
  if (mode === 'share') return s.bFlaggedUsd;
  return s.state === 'rated' ? Math.max(0, s.markout.bUsd['5'] ?? 0) : 0;
}

export function buildFlowView(payload: FlowPayload | null, scope: PlatformScope, mode: FlowMode = 'share'): FlowView {
  const t = flowThresholds(payload);
  const mt = markoutThresholds(payload);
  const toxicEntries = Object.entries(payload?.toxic_classes ?? {});
  const toxic = new Set(toxicEntries.map(([k]) => k));
  const perPlatform = scope !== 'ALL' && splitsPlatforms(payload);
  const valid = (payload?.by_symbol ?? []).filter((r) => r && typeof r.symbol === 'string' && r.symbol);
  const all = valid.map((r) => rowOf(r, r, mode, t, mt, toxic));
  const symbols = perPlatform
    ? valid.flatMap((r) => {
      const f = figuresOf(r, scope);
      return f ? [rowOf(r, f, mode, t, mt, toxic)] : [];
    })
    : all.filter((s) => inScope(s, scope));

  // Most against the broker first, then the rest with a reading (by flow),
  // then the thin ones, then those with no B-Book flow.
  const order: Record<FlowState, number> = { rated: 0, thin: 1, no_b_flow: 2 };
  symbols.sort((a, b) =>
    Number(rankValue(b, mode) > 0) - Number(rankValue(a, mode) > 0) ||
    rankValue(b, mode) - rankValue(a, mode) ||
    order[a.state] - order[b.state] ||
    b.flowUsd - a.flowUsd ||
    a.symbol.localeCompare(b.symbol),
  );

  const bandCounts: Record<Band, number> = { low: 0, warning: 0, high: 0, critical: 0 };
  let rank = 0, rated = 0, noBFlow = 0, thin = 0;
  for (const s of symbols) {
    if (rankValue(s, mode) > 0) s.rank = ++rank;
    if (s.band) bandCounts[s.band]++;
    if (s.state === 'rated') rated++; else if (s.state === 'thin') thin++; else noBFlow++;
  }

  const tt = totalsOf(payload, perPlatform ? scope : 'ALL');
  const bShare = optNum(tt?.b_toxic_share);
  const totalMarkout = markoutOf(tt);
  const bookMk = totalMarkout.bBps['5'];
  const hasMarkout = !!payload && (payload.markout_bands !== undefined || (!!tt && 'markout_fills' in tt));
  return {
    mode,
    asOf: parseTime(payload?.as_of),
    windowSec: optNum(payload?.window_sec),
    thresholds: t,
    markoutThresholds: mt,
    minFills: optNum(payload?.min_fills),
    hasMarkout,
    toxicClasses: toxicEntries.map(([cls, v]) => ({
      cls,
      minConfidence: optNum(v?.min_confidence),
      minRiskLevel: typeof v?.min_risk_level === 'string' ? v.min_risk_level : null,
    })),
    platforms: (payload?.platforms ?? []).map(toPlatform).filter((p): p is Platform => p !== null),
    perPlatform,
    symbols,
    bySymbol: new Map(symbols.map((s) => [s.symbol, s] as const)),
    hiddenByScope: all.length - symbols.length,
    totals: {
      flowUsd: num(tt?.flow_usd), flaggedUsd: num(tt?.flagged_flow_usd),
      bFlowUsd: num(tt?.b_flow_usd), bFlaggedUsd: num(tt?.b_flagged_flow_usd),
      share: optNum(tt?.toxic_share), bShare,
      fills: num(tt?.fills), flaggedFills: num(tt?.flagged_fills), unpricedFills: num(tt?.unpriced_fills),
      accounts: num(tt?.accounts), flaggedAccounts: num(tt?.flagged_accounts),
      unclassifiedAccounts: num(tt?.unclassified_accounts), unclassifiedUsd: num(tt?.unclassified_flow_usd),
      markout: totalMarkout,
      cost: costOf(tt as Parameters<typeof costOf>[0]),
    },
    bookBand: mode === 'share'
      ? (bShare === null ? null : bandOf(bShare, t))
      : (bookMk === null ? null : bandOf(markoutLevel(bookMk), mt)),
    bandCounts,
    rated,
    noBFlow,
    thin,
  };
}

// History readers. `scope` reads the platform's own figures when the payload
// splits them, so the sparklines and trends follow the platform chosen.

/** Whole-book B-Book toxic share of a payload (for the observed-history line). */
export function bookShareOf(payload: FlowPayload, scope: PlatformScope = 'ALL'): number {
  return optNum(totalsOf(payload, scope)?.b_toxic_share) ?? 0;
}

/** Whole-book B-Book markout at 5 s, bps; 0 when not measured. */
export function bookMarkoutOf(payload: FlowPayload, scope: PlatformScope = 'ALL'): number {
  return optNum(totalsOf(payload, scope)?.b_markout_bps?.['5']) ?? 0;
}

function symbolFigures(payload: FlowPayload, symbol: string, scope: PlatformScope): FlowPlatformFigures | null {
  const r = payload.by_symbol?.find((x) => x.symbol === symbol);
  return r ? figuresOf(r, scope) : null;
}

/** One instrument's B-Book toxic share in a payload; 0 when absent. */
export function shareOf(payload: FlowPayload, symbol: string, scope: PlatformScope = 'ALL'): number {
  return optNum(symbolFigures(payload, symbol, scope)?.b_toxic_share) ?? 0;
}

/** One instrument's B-Book markout at 5 s, bps; 0 when absent. */
export function markoutOfSymbol(payload: FlowPayload, symbol: string, scope: PlatformScope = 'ALL'): number {
  return optNum(symbolFigures(payload, symbol, scope)?.b_markout_bps?.['5']) ?? 0;
}

/** "+0.42 bps" / "−0.10 bps" / "—". */
export function fmtBps(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  const a = Math.abs(v);
  return `${sign}${a < 10 ? a.toFixed(2) : a.toFixed(1)} bps`;
}

/** "60 min", "4 h", "24 h". */
export function fmtWindow(sec: number | null): string {
  if (sec === null || !(sec > 0)) return '—';
  if (sec % 3600 === 0) return `${sec / 3600} h`;
  return `${Math.round(sec / 60)} min`;
}

/** Classifier names in plain words. */
export function classLabel(cls: string | null | undefined): string {
  if (!cls) return 'Not classified';
  const map: Record<string, string> = {
    ARBITRAGE: 'Arbitrage', REBATE_ABUSE: 'Rebate abuse', NEWS_TRADER: 'News trader',
    SCALPER: 'Scalper', EA_SCALPER: 'EA scalper', EA_TRADER: 'EA trader', EA_BOT: 'EA bot',
    MANUAL: 'Manual', DAY_TRADER: 'Day trader', SWING_TRADER: 'Swing trader',
    INSUFFICIENT_DATA: 'Too few trades', UNKNOWN: 'Unknown',
  };
  return map[cls] ?? cls.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}
